import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { CUSTOMER_REQUESTS_BASE } from "../../../routes"
import { formatInt, localeForLanguage } from "../customerDashboardStore"
import type { DashboardWidgetProps } from "../customerWidgetRegistry"
import { requestActionPath } from "./requestActionPath"
import { RequestTopRow } from "./RequestTopRow"
import { SummaryLinkRow } from "./SummaryLinkRow"

const MC_BASE = "/material-compliance"

// Mirrors the summary-row pattern of both existing widgets built this way
// (supplier HandedBackWidget, customer ConformityWidget) — every row is a
// Link, not just the ones the brief called out by name.
//  - blockedRequests / openQuestions stay within customer-studio: both are
//    facets of the SAME `gaps` bucket predicate
//    (mc_customer_dashboard_repository.py's BUCKET_PREDICATES['gaps'] — the
//    dashboard's `gaps` bucket count and blockedRequests are literally the
//    same number, per mc_customer_dashboard_service.py). openQuestions counts
//    individual gap answers rather than questionnaires, but there's no
//    dedicated gap-answers list view to send it to, so it links to the same
//    bucket=gaps filtered request list as the closest available target.
//  - stagedInquiries crosses into supplier-studio (draft supplier inquiries,
//    trigger_type='customer_gap') — `?status=draft&trigger_type=customer_gap`
//    verified against InquiryListPanel.tsx's status/trigger_type query
//    params, ANDed together server-side (mc_supplier_inquiry_repository's
//    trigger_type filter): without trigger_type this linked to EVERY draft
//    inquiry, not just the ones this widget's count actually covers.
const ROWS: {
  labelKey: TranslationKeys
  value: (s: DashboardWidgetProps["stats"]) => number
  to: string
  warn?: boolean
}[] = [
  {
    labelKey: "MC_CSD_GAP_REQUESTS",
    value: (s) => s.gaps.blockedRequests,
    to: `${CUSTOMER_REQUESTS_BASE}?bucket=gaps`,
    warn: true,
  },
  {
    labelKey: "MC_CSD_GAP_QUESTIONS",
    value: (s) => s.gaps.openQuestions,
    to: `${CUSTOMER_REQUESTS_BASE}?bucket=gaps`,
  },
  {
    labelKey: "MC_CSD_GAP_STAGED",
    value: (s) => s.gaps.stagedInquiries,
    to: `${MC_BASE}/supplier-studio/inquiry?status=draft&trigger_type=customer_gap`,
  },
]

export function GapsWidget({ stats, loadFailed }: DashboardWidgetProps) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  // Fix B.1: nothing blocked anywhere — the 3 zero rows are noise, not
  // signal. Only the fully-empty case collapses to the friendly line;
  // gapsTop being empty on its own (the common "no example rows yet" case)
  // still renders the 3 summary rows as before.
  const allEmpty =
    stats.gaps.blockedRequests === 0 &&
    stats.gaps.openQuestions === 0 &&
    stats.gaps.stagedInquiries === 0 &&
    stats.gapsTop.length === 0
  if (allEmpty) {
    // After a failed load the zeros are the fallback: no "nothing is blocked".
    if (loadFailed) return null
    return (
      <div className="py-6 text-sm text-grey5">
        {translate("MC_CSD_GAPS_EMPTY")}
      </div>
    )
  }
  return (
    <div>
      {ROWS.map((row) => (
        <SummaryLinkRow
          key={row.labelKey}
          labelKey={row.labelKey}
          value={row.value(stats)}
          to={row.to}
          warn={row.warn}
        />
      ))}
      {/* Spec §2.6: "plus top 2 example rows with actions". Every gapsTop row
          is, by definition, at the gaps step (backend: get_gaps_top hardcodes
          step='gaps'/metricKind='gap_questions') — same RequestTopRow pattern
          as WaitingWidget, including its Finding-6 title/subtitle-reference
          dedupe, but with a fixed action/metric label instead of a per-row
          CASE. Empty gapsTop renders nothing extra; the 3 summary rows above
          stay as-is. */}
      {stats.gapsTop.map((item) => {
        // The row's actual CTA — routes through the confirm/import flow
        // instead in the (defensive, rarely hit for gaps rows) case the row
        // is still at an intake state (see requestActionPath's docstring).
        const actionTo = requestActionPath(item)
        return (
          <RequestTopRow
            key={item.questionnaireId}
            item={item}
            subtitleTail={translate("MC_CSD_ROW_CLOSE_GAPS")}
            metric={
              <span className={cn(item.overdue && "text-redLight")}>
                {formatInt(item.metric, locale)}
              </span>
            }
            metricLabel={translate("MC_CSD_ROW_GAP_QUESTIONS")}
            action={{ label: translate("MC_CSD_ROW_CLOSE_GAPS"), to: actionTo }}
          />
        )
      })}
    </div>
  )
}
